import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { UIElement } from '../ui-element';
import type { ElementConfig } from '../ui-element.type';
import { keyCodes, isTabPressed, TabIndex } from '../../global/keyboard';
import { UIOptionListType, UIOptionListTypes } from './ui-option.types';
import { Children } from '../../decorators/child.decorator';
import { Digest } from '../../global/digest';
import styles from './ui-option-list.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIOptionList
 * @element ui-option-list
 * @classdesc Accessible listbox container for <code>ui-option-*</code> elements.
 * Wraps children in semantic <code>&lt;li role="option"&gt;</code> items,
 * manages roving tabindex and keyboard navigation (arrow keys, Home, End, type-ahead).
 * @fires event:option-select Fires when an option is selected.
 * Detail: <code>{ element: Element | null }</code>
 * @property {string} [label] {@attr label} Accessible label for the listbox
 * (<code>aria-label</code> on the inner <code>&lt;ul&gt;</code>).
 * @property {UIOptionListType} [type="listbox"] {@attr type} Interaction model.
 *  {@desc listbox: value selection, items have aria-selected}
 *  {@desc menu: action items, items are wrapped in button or a}
 * @slot
 * @example
 * <ui-option-list label="Select account">
 *   <ui-option-account holder="Tiina Puu"></ui-option-account>
 *   <ui-option-account holder="Mari Muul" selected></ui-option-account>
 * </ui-option-list>
 * @example
 * <ui-option-list type="menu" label="Actions">
 *   <ui-option-basic>Delete</ui-option-basic>
 *   <ui-option-basic href="/settings">Settings</ui-option-basic>
 * </ui-option-list>
 */
@Component({
    tag: 'ui-option-list',
    styles: styles,
})
export class UIOptionList extends UIElement {
    static readonly Type: UIOptionListTypes = {
        Listbox: 'listbox',
        Menu: 'menu',
    };

    @Attribute(String) label?: string;
    @Attribute(String) type: UIOptionListType = UIOptionList.Type.Listbox;
    @Children('.ui-option-list__option') items?: Array<HTMLElement>;

    private keyDownHandler?: (e: KeyboardEvent) => void;
    private clickHandler?: (e: MouseEvent) => void;

    /**
     * Determines if the list is a menu (action items) or listbox (value selection).
     */
    isMenu(): boolean {
        return this.type === UIOptionList.Type.Menu;
    }

    /**
     * Returns all focusable <li> items (excluding disabled ones).
     * @private
     */
    private getOptions(): Array<HTMLElement> {
        return Array.from(
            this.querySelectorAll<HTMLElement>(
                '.ui-option-list__option:not(.-disabled):not(.-hidden):not([aria-hidden])'
            )
        );
    }

    /**
     * Moves roving tabindex to the given <li> and focuses it.
     * @param {HTMLElement} item
     */
    private setActiveItem(item: HTMLElement): void {
        this.items?.forEach((el) =>
            el.setAttribute('tabindex', TabIndex.Inactive)
        );
        item.setAttribute('tabindex', TabIndex.Active);
        item.focus();
    }

    /**
     * Keyboard navigation: arrow keys, Home, End, Enter/Space, type-ahead.
     * @param {KeyboardEvent} event
     */
    private handleKeyDown(event: KeyboardEvent): void {
        const options = this.getOptions();
        const currentIndex = options.indexOf(
            document.activeElement as HTMLElement
        );

        // When rendered inside a modal (e.g. ui-chip popup), Tab and
        // Escape should close the modal and return focus to the invoking element
        if (
            this.closest('ui-modal') &&
            (isTabPressed(event) || keyCodes.ESCAPE === event.keyCode)
        ) {
            event.preventDefault();
            this.dispatchCustomEvent('modal-close', {});
            return;
        }

        if (keyCodes.UP === event.keyCode || keyCodes.DOWN === event.keyCode) {
            const step = keyCodes.UP === event.keyCode ? -1 : 1;
            let nextIndex = currentIndex + step;
            if (nextIndex < 0) {
                nextIndex = nextIndex + options.length;
            }
            if (nextIndex > options.length - 1) {
                nextIndex = nextIndex - options.length;
            }
            while (options[nextIndex].classList.contains('-disabled')) {
                if (keyCodes.UP === event.keyCode) {
                    nextIndex--;
                }
                if (
                    keyCodes.DOWN === event.keyCode &&
                    ++nextIndex > options.length - 1
                ) {
                    nextIndex = 0;
                }
            }

            if (options[nextIndex]) {
                options[nextIndex].focus();
            }
            event.preventDefault();
        }

        if (
            keyCodes.ENTER === event.keyCode ||
            keyCodes.SPACE === event.keyCode
        ) {
            event.preventDefault();
            const focused = document.activeElement as HTMLElement;
            if (focused?.closest('ui-option-list') === this) {
                const option = focused.firstElementChild as HTMLElement | null;
                (option ?? focused).click();
            }
            return;
        }
    }

    /**
     * Click handler: updates selected state and fires option-select event.
     * @fires event:option-select
     * @param {MouseEvent} event
     */
    private handleClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const li = target.closest<HTMLElement>('.ui-option-list__option');
        if (!li || li.classList.contains('-disabled')) {
            return;
        }

        if (!this.isMenu()) {
            this.items?.forEach((item) => {
                item.classList.remove('-selected');
                item.setAttribute('aria-selected', 'false');
                item.firstElementChild?.removeAttribute('selected');
            });

            li.classList.add('-selected');
            li.setAttribute('aria-selected', 'true');
            li.firstElementChild?.setAttribute('selected', '');
        }

        this.setActiveItem(li);

        this.dispatchCustomEvent('option-select', {
            element: li.firstElementChild,
        });
    }

    /**
     * Iterates children, handles optgroup wrapping, and delegates non-group
     * items to the provided factory callback.
     * @param {Array<HTMLElement>} children
     * @param {function} buildOptionItem - factory for a single non-group item
     */
    private buildItems(
        children: Array<HTMLElement>,
        buildOptionItem: (
            child: HTMLElement,
            index: number,
            groupLabelId: string | null
        ) => ElementConfig
    ): Array<ElementConfig> {
        const result: Array<ElementConfig> = [];
        let currentGroupItems: Array<ElementConfig> | null = null;
        let currentGroupLabelId: string | null = null;

        children.forEach((child, index) => {
            if (child.hasAttribute('optgroup')) {
                const labelId = Digest.randomId();
                currentGroupLabelId = labelId;
                child.removeAttribute('selectable');
                child.classList.add('ui-option-list__group-label');

                const groupItems: Array<ElementConfig> = [
                    {
                        tagName: 'li',
                        attributes: { role: 'presentation', id: labelId },
                        classList: {
                            'ui-option-list__group-label-wrapper': true,
                        },
                        children: [child],
                    },
                ];
                currentGroupItems = groupItems;

                result.push({
                    tagName: 'li',
                    classList: { 'ui-option-list__group': true },
                    attributes: { role: 'presentation' },
                    children: [
                        {
                            tagName: 'ul',
                            classList: { 'ui-option-list__group-body': true },
                            attributes: {
                                'role': 'group',
                                'aria-labelledby': labelId,
                            },
                            children: groupItems,
                        },
                    ],
                });
            } else {
                const item = buildOptionItem(child, index, currentGroupLabelId);
                (currentGroupItems ?? result).push(item);
            }
        });

        return result;
    }

    /**
     * Builds listbox items for the option list.
     * @param {Array<HTMLElement>} children
     * @param {number} firstTabbableIndex
     * @returns {Array<ElementConfig>}
     */
    private buildListboxItems(
        children: Array<HTMLElement>,
        firstTabbableIndex: number
    ): Array<ElementConfig> {
        return this.buildItems(children, (child, index, groupLabelId) => {
            const isSelected = child.hasAttribute('selected');
            const isDisabled = child.hasAttribute('disabled');
            child.removeAttribute('tabindex');
            child.removeAttribute('role');
            child.removeAttribute('aria-selected');
            child.setAttribute('selectable', '');
            return {
                tagName: 'li',
                classList: {
                    '-selected': isSelected,
                    '-disabled': isDisabled,
                    'ui-option-list__option': true,
                },
                attributes: {
                    'role': 'option',
                    'tabindex':
                        index === firstTabbableIndex
                            ? TabIndex.Active
                            : TabIndex.Inactive,
                    'aria-selected': isSelected ? 'true' : 'false',
                    ...(isDisabled ? { 'aria-disabled': 'true' } : {}),
                    ...(groupLabelId
                        ? { 'aria-describedby': groupLabelId }
                        : {}),
                },
                children: [child],
            };
        });
    }

    /**
     * Builds menu items for the option list.
     * @param {Array<HTMLElement>} children
     * @param {number} firstTabbableIndex
     * @returns {Array<ElementConfig>}
     */
    private buildMenuItems(
        children: Array<HTMLElement>,
        firstTabbableIndex: number
    ): Array<ElementConfig> {
        return this.buildItems(children, (child, index, groupLabelId) => {
            const isDisabled = child.hasAttribute('disabled');
            child.removeAttribute('tabindex');
            return {
                tagName: 'li',
                classList: {
                    '-disabled': isDisabled,
                    'ui-option-list__option': true,
                },
                attributes: {
                    role: 'menuitem',
                    tabindex:
                        index === firstTabbableIndex
                            ? TabIndex.Active
                            : TabIndex.Inactive,
                    ...(isDisabled ? { 'aria-disabled': 'true' } : {}),
                    ...(groupLabelId
                        ? { 'aria-describedby': groupLabelId }
                        : {}),
                },
                children: [child],
            };
        });
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const children = this.detachChildNodes().filter(
            (node): node is HTMLElement =>
                node instanceof HTMLElement &&
                node.localName.startsWith('ui-option-')
        );
        const selectedIndex = children.findIndex(
            (child) =>
                !child.hasAttribute('optgroup') &&
                child.hasAttribute('selected')
        );
        const firstTabbableIndex =
            selectedIndex >= 0
                ? selectedIndex
                : children.findIndex(
                      (child) => !child.hasAttribute('optgroup')
                  );

        const items = this.isMenu()
            ? this.buildMenuItems(children, firstTabbableIndex)
            : this.buildListboxItems(children, firstTabbableIndex);

        this.insertElements([
            {
                tagName: 'ul',
                classList: {
                    'ui-option-list__body': true,
                },
                attributes: {
                    role: this.isMenu() ? 'menu' : 'listbox',
                    ...(this.label ? { 'aria-label': this.label } : {}),
                },
                children: items,
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.keyDownHandler = this.handleKeyDown.bind(this);
        this.clickHandler = this.handleClick.bind(this);
        this.addEventListener('keydown', this.keyDownHandler);
        this.addEventListener('click', this.clickHandler);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        if (this.keyDownHandler) {
            this.removeEventListener('keydown', this.keyDownHandler);
        }
        if (this.clickHandler) {
            this.removeEventListener('click', this.clickHandler);
        }
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        if (this.keyDownHandler) {
            this.addEventListener('keydown', this.keyDownHandler);
        }
        if (this.clickHandler) {
            this.addEventListener('click', this.clickHandler);
        }
    }
}
